package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.ClassKind
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * Stage 6：class 内 fun 作用域守卫 + 成员重名检测。
 *
 * 设计目标：
 * - class 内 fun 是"近闭包"对象：仅可访问本 class 字段、本 class 其它 fun、
 *   字面量、内置函数与 lambda 隐式参数 `it`；不得访问 screen.state / screen 级 fun /
 *   顶层 var / source 形参等宿主作用域对象。
 * - 违反约束时上报 [DslError.E790_CLASS_BODY_FUN_SCOPE_VIOLATION]，
 *   消息携带 class 名 / fun 名 / 被访问的宿主对象名三段，便于定位。
 * - 字段名与 fun 名共享命名空间，重名上报 [DslError.E791_CLASS_MEMBER_DUPLICATE]。
 */
class ClassBodyChecker(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        // Stage 7b：收集所有 OBJECT kind 的顶层 class 名作为合法 IdentRef 起点；
        // Stage 8 嵌套 OBJECT 通过 MemberAccess 链访问，外层名仍是顶层 OBJECT，因此
        // 嵌套 OBJECT 的名字无需进入 objectClassNames（它们不会出现在 IdentRef 位置）
        val objectClassNames = screen.classDecls
            .filter { it.kind == ClassKind.OBJECT }
            .map { it.name }
            .toSet()
        for (decl in screen.classDecls) {
            checkClassRecursive(decl, objectClassNames)
        }
    }

    /**
     * Stage 8：递归对 [decl] 自身及其 [ClassDecl.nestedClasses] 调用 [checkClass]。
     *
     * 嵌套 class 的 fun body 内仍然适用与顶层 class 完全一致的"近闭包"作用域守卫。
     */
    private fun checkClassRecursive(decl: ClassDecl, objectClassNames: Set<String>) {
        checkClass(decl, objectClassNames)
        for (nested in decl.nestedClasses) {
            checkClassRecursive(nested, objectClassNames)
        }
    }

    /**
     * 校验单个 class：
     * 1. 成员重名检测（字段 vs fun / fun vs fun）
     * 2. 对每个 fun body 做作用域守卫
     */
    private fun checkClass(decl: ClassDecl, objectClassNames: Set<String>) {
        if (decl.funs.isEmpty()) return

        // 收集合法符号集合
        val fieldNames = decl.fields.map { it.name }.toSet()
        val funNames = decl.funs.map { it.name }.toSet()

        // 成员重名检测
        checkDuplicateMembers(decl, fieldNames)

        // 对每个 fun 做作用域守卫
        for (fun_ in decl.funs) {
            val legalSymbols = buildLegalSymbols(fieldNames, funNames, fun_, objectClassNames)
            walkFunBody(decl.name, fun_.name, fun_.body, legalSymbols)
        }
    }

    /**
     * 检测字段与 fun 同名、fun 之间同名。
     */
    private fun checkDuplicateMembers(decl: ClassDecl, fieldNames: Set<String>) {
        // fun 名与字段名冲突
        for (fun_ in decl.funs) {
            if (fun_.name in fieldNames) {
                diag.error(DslError.E791_CLASS_MEMBER_DUPLICATE, fun_.position, decl.name, fun_.name)
            }
        }
        // fun 之间同名
        val seenFuns = mutableSetOf<String>()
        for (fun_ in decl.funs) {
            if (!seenFuns.add(fun_.name)) {
                diag.error(DslError.E791_CLASS_MEMBER_DUPLICATE, fun_.position, decl.name, fun_.name)
            }
        }
    }

    /**
     * 构建 fun body 内合法的标识符集合：
     * - 本 class 字段名
     * - 本 class 所有 fun 名
     * - 当前 fun 的形参名
     * - 内置函数白名单
     * - `it`（lambda 隐式参数）
     * - `_`（丢弃占位）
     * - Stage 7b：所有 user object 名（允许跨 object 调用）
     */
    private fun buildLegalSymbols(
        fieldNames: Set<String>,
        funNames: Set<String>,
        fun_: FunDecl,
        objectClassNames: Set<String>,
    ): Set<String> {
        val symbols = mutableSetOf<String>()
        symbols += fieldNames
        symbols += funNames
        symbols += fun_.params
        symbols += BUILTIN_WHITELIST
        symbols += "it"
        symbols += "_"
        symbols += objectClassNames
        return symbols
    }

    /**
     * 遍历 fun body 中所有语句，对表达式做作用域检查。
     */
    private fun walkFunBody(className: String, funName: String, body: List<StmtAst>, legalSymbols: Set<String>) {
        // 可变集合：body 内 var 声明也是合法的局部变量
        val locals = legalSymbols.toMutableSet()
        for (stmt in body) {
            walkStmt(className, funName, stmt, locals)
        }
    }

    private fun walkStmt(className: String, funName: String, stmt: StmtAst, locals: MutableSet<String>) {
        when (stmt) {
            is StmtAst.ExprStmt -> walkExpr(className, funName, stmt.call, locals)
            is StmtAst.VarDeclStmt -> {
                walkExpr(className, funName, stmt.initializer, locals)
                locals += stmt.name
            }
            is StmtAst.AssignStmt -> {
                walkLValue(className, funName, stmt.lhs, locals)
                walkExpr(className, funName, stmt.rhs, locals)
            }
            is StmtAst.IfStmt -> {
                walkExpr(className, funName, stmt.cond, locals)
                for (s in stmt.thenBody) walkStmt(className, funName, s, locals)
                for (s in stmt.elseBody) walkStmt(className, funName, s, locals)
            }
            is StmtAst.ReturnStmt -> {
                stmt.value?.let { walkExpr(className, funName, it, locals) }
            }
            is StmtAst.ForInStmt -> {
                walkExpr(className, funName, stmt.iterable, locals)
                val innerLocals = locals.toMutableSet()
                innerLocals += stmt.varName
                for (s in stmt.body) walkStmt(className, funName, s, innerLocals)
            }
            is StmtAst.WhileStmt -> {
                walkExpr(className, funName, stmt.cond, locals)
                for (s in stmt.body) walkStmt(className, funName, s, locals)
            }
            is StmtAst.FunDeclStmt -> {
                // 局部 fun 声明：fun 名加入合法集合；body 在新作用域中递归检查，
                // 形参也并入新作用域，但不污染外层 locals
                locals += stmt.decl.name
                val innerLocals = locals.toMutableSet()
                innerLocals += stmt.decl.params
                for (s in stmt.decl.body) walkStmt(className, funName, s, innerLocals)
            }
            is StmtAst.TryStmt -> {
                for (s in stmt.tryBody) walkStmt(className, funName, s, locals)
                val catchLocals = locals.toMutableSet()
                catchLocals += stmt.catchName
                for (s in stmt.catchBody) walkStmt(className, funName, s, catchLocals)
                for (s in stmt.finallyBody) walkStmt(className, funName, s, locals)
            }
            // batch 语句：递归检查 body 内每条语句（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> {
                for (s in stmt.body) walkStmt(className, funName, s, locals)
            }
            is StmtAst.WhenStmt -> {
                // WhenStmt 仅是 WhenExpr 的语句形态包装，walkExpr 的 WhenExpr 分支
                // 已遍历 subject / branches.values / branches.body / elseBody，无需重复
                walkExpr(className, funName, stmt.whenExpr, locals)
            }
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    private fun walkLValue(className: String, funName: String, lv: LValueAst, locals: MutableSet<String>) {
        when (lv) {
            is LValueAst.StateLValue -> reportViolation(className, funName, "state.${lv.name}", lv.position)
            is LValueAst.SessionLValue -> reportViolation(className, funName, "session.${lv.name}", lv.position)
            is LValueAst.PersistLValue -> reportViolation(className, funName, "persist.${lv.name}", lv.position)
            is LValueAst.LocalLValue -> Unit // 局部变量赋值合法
            is LValueAst.IndexLValue -> {
                walkExpr(className, funName, lv.target, locals)
                walkExpr(className, funName, lv.index, locals)
            }
        }
    }

    private fun walkExpr(className: String, funName: String, expr: ExprAst, locals: MutableSet<String>) {
        when (expr) {
            // 直接违规：state / props / theme 引用
            is ExprAst.StateRef -> reportViolation(className, funName, "state.${expr.name}", expr.position)
            is ExprAst.SessionRef -> reportViolation(className, funName, "session.${expr.name}", expr.position)
            is ExprAst.PersistRef -> reportViolation(className, funName, "persist.${expr.name}", expr.position)
            is ExprAst.DerivedRef -> reportViolation(className, funName, "derived.${expr.name}", expr.position)
            is ExprAst.PropsRef -> reportViolation(className, funName, "props.${expr.name}", expr.position)
            is ExprAst.ThemeRef -> reportViolation(className, funName, "theme.${expr.key}", expr.position)

            // 标识符引用：检查是否在合法集合内
            is ExprAst.IdentRef -> {
                if (expr.name !in locals) {
                    reportViolation(className, funName, expr.name, expr.position)
                }
            }
            is ExprAst.LocalRef -> {
                if (expr.name !in locals) {
                    reportViolation(className, funName, expr.name, expr.position)
                }
            }

            // MemberCall：target 为宿主对象时上报
            is ExprAst.MemberCall -> {
                val target = expr.target
                if (target is ExprAst.IdentRef && target.name in HOST_OBJECTS) {
                    reportViolation(className, funName, target.name, expr.position)
                } else {
                    walkExpr(className, funName, expr.target, locals)
                }
                for (arg in expr.args) walkExpr(className, funName, arg, locals)
                for (na in expr.namedArgs) walkExpr(className, funName, na.value, locals)
            }

            // MemberAccess：target 为宿主对象时上报
            is ExprAst.MemberAccess -> {
                val target = expr.target
                if (target is ExprAst.IdentRef && target.name in HOST_OBJECTS) {
                    reportViolation(className, funName, target.name, expr.position)
                } else {
                    walkExpr(className, funName, expr.target, locals)
                }
            }

            // 递归遍历复合表达式
            is ExprAst.BinaryOp -> {
                walkExpr(className, funName, expr.left, locals)
                walkExpr(className, funName, expr.right, locals)
            }
            is ExprAst.UnaryOp -> walkExpr(className, funName, expr.operand, locals)
            is ExprAst.Ternary -> {
                walkExpr(className, funName, expr.cond, locals)
                walkExpr(className, funName, expr.thenExpr, locals)
                walkExpr(className, funName, expr.elseExpr, locals)
            }
            is ExprAst.IfExpr -> {
                walkExpr(className, funName, expr.cond, locals)
                for (s in expr.thenBody) walkStmt(className, funName, s, locals)
                for (s in expr.elseBody) walkStmt(className, funName, s, locals)
            }
            is ExprAst.Elvis -> {
                walkExpr(className, funName, expr.left, locals)
                walkExpr(className, funName, expr.right, locals)
            }
            is ExprAst.CallExpr -> {
                walkExpr(className, funName, expr.callee, locals)
                for (arg in expr.args) walkExpr(className, funName, arg, locals)
                for (na in expr.namedArgs) walkExpr(className, funName, na.value, locals)
            }
            is ExprAst.LambdaExpr -> {
                val lambdaLocals = locals.toMutableSet()
                lambdaLocals += expr.params
                for (s in expr.body) walkStmt(className, funName, s, lambdaLocals)
            }
            is ExprAst.ListLiteralExpr -> {
                for (e in expr.elements) walkExpr(className, funName, e, locals)
            }
            is ExprAst.MapLiteralExpr -> {
                for (entry in expr.entries) walkExpr(className, funName, entry.value, locals)
            }
            is ExprAst.IndexExpr -> {
                walkExpr(className, funName, expr.target, locals)
                walkExpr(className, funName, expr.index, locals)
            }
            is ExprAst.SafeMemberAccess -> walkExpr(className, funName, expr.target, locals)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> walkExpr(className, funName, expr.target, locals)
            is ExprAst.SafeMemberCall -> {
                walkExpr(className, funName, expr.target, locals)
                for (arg in expr.args) walkExpr(className, funName, arg, locals)
            }
            is ExprAst.WhenExpr -> {
                walkExpr(className, funName, expr.subject, locals)
                for (branch in expr.branches) {
                    for (v in branch.values) walkExpr(className, funName, v, locals)
                    for (s in branch.body) walkStmt(className, funName, s, locals)
                }
                expr.elseBody?.let { for (s in it) walkStmt(className, funName, s, locals) }
            }
            is ExprAst.RangeExpr -> {
                walkExpr(className, funName, expr.start, locals)
                walkExpr(className, funName, expr.endValue, locals)
            }
            is ExprAst.Interp -> {
                for (part in expr.parts) {
                    if (part is ExprAst.Interp.InterpPart.ExprPart) {
                        walkExpr(className, funName, part.expr, locals)
                    }
                }
            }
            is ExprAst.BuiltinCallExpr -> {
                for (arg in expr.args) walkExpr(className, funName, arg, locals)
                for (na in expr.namedArgs) walkExpr(className, funName, na.value, locals)
            }
            is ExprAst.IsExpr -> walkExpr(className, funName, expr.target, locals)
            is ExprAst.FunRefExpr -> {
                // Stage 7c：NativeClassResolverPass 已把 ObjectName.method(...) lower 为
                // FunRefExpr("ObjectName#methodName")。此处把含分隔符的引用拆出 ObjectName，
                // 若位于本 screen 的 user object 名集合内（locals 已合并），视为合法调用。
                val sep = top.wcpe.allincore.dsl.compiler.semantic.`native`.methods
                    .UserObjectMethodDescriptor.FUN_REF_SEPARATOR
                val sepIdx = expr.name.indexOf(sep)
                val ownerOk = sepIdx > 0 && expr.name.substring(0, sepIdx) in locals
                if (!ownerOk && expr.name !in locals) {
                    reportViolation(className, funName, expr.name, expr.position)
                }
                for (arg in expr.boundArgs) walkExpr(className, funName, arg, locals)
                for (na in expr.boundNamedArgs) walkExpr(className, funName, na.value, locals)
            }
            is ExprAst.AwaitExpr -> {
                expr.netRequestCall?.let { walkExpr(className, funName, it, locals) }
                expr.suspendCall?.let { walkExpr(className, funName, it, locals) }
                expr.tailExpr?.let { walkExpr(className, funName, it, locals) }
            }
            is ExprAst.NetRequestCall -> {
                // net.request 在 class fun 内属于宿主调用，但此处已由 MemberCall 路径拦截；
                // 若直接出现 NetRequestCall 节点（lower 后），上报 net 违规
                reportViolation(className, funName, "net", expr.position)
            }
            is ExprAst.NetSubscribeCall -> {
                reportViolation(className, funName, "net", expr.position)
            }
            // 字面量与 null 无需检查
            is ExprAst.Literal -> Unit
            is ExprAst.NullLit -> Unit
        }
    }

    private fun reportViolation(className: String, funName: String, target: String, position: SourcePosition) {
        diag.error(DslError.E790_CLASS_BODY_FUN_SCOPE_VIOLATION, position, className, funName, target)
    }

    companion object {
        /**
         * 内置函数白名单（class fun 内允许直接调用的全局函数集合）。
         *
         * 共享 [BuiltinFunctions.NAMES] 单一定义，与
         * [top.wcpe.allincore.dsl.compiler.parse.ExpressionParser] 共用，
         * 避免两处重复维护导致漂移。
         */
        private val BUILTIN_WHITELIST: Set<String> = BuiltinFunctions.NAMES

        /** 宿主对象名：class fun 内不允许以这些为 target 做 MemberCall/MemberAccess。 */
        private val HOST_OBJECTS: Set<String> = setOf("net", "local", "vanilla")
    }
}
